import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowLeft,
  Brain,
  Check,
  Clock3,
  LoaderCircle,
  Music2,
  Pause,
  Play,
  RotateCcw,
  Star,
  Volume2,
} from 'lucide-react';
import type { AppState, Child, KnowledgeType, PoemSong, ReviewEvent, ReviewResult } from '../types';
import { getChildProgress, getLearningPlanIds, getLearningQueue, getMemoryLabel } from '../lib/srs';
import { lookupPoemSong } from '../lib/storage';
import { ChildAvatar } from '../components/ui';
import { WordLearningPanel } from '../components/WordLearningPanel';

const typeTitles: Record<KnowledgeType, string> = {
  character: '汉字记忆',
  poem: '古诗记忆',
  word: '英文记忆'
};

const childVoiceNamePatterns: Record<'zh-CN' | 'en-US', RegExp> = {
  'zh-CN': /xiaoyou|xiaoshuang|xiaoxiao|xiaoyi|xiaomeng|xiaohan|yaoyao|huihui|ting-ting|tingting|meijia|mei-jia|female|child|女|童|丫丫|晓悠|晓双/i,
  'en-US': /ana|aria|jenny|samantha|susan|zira|michelle|emma|ava|libby|female|child|kid|女|童/i
};

function getPreferredSpeechVoice(voices: SpeechSynthesisVoice[], lang: 'zh-CN' | 'en-US') { 
  let preferred: SpeechSynthesisVoice | null = null;
  let preferredScore = -1;
  for (const voice of voices) {
    const voiceLang = voice.lang.replace('_', '-').toLowerCase();
    if (!voiceLang.startsWith(lang.toLowerCase())) continue;
    const name = `${voice.name} ${voice.voiceURI}`;
    let score = 0;
    if (childVoiceNamePatterns[lang].test(name)) score += 6;
    if (/natural|neural|premium|enhanced/i.test(name)) score += 2;
    if (voice.localService) score += 1;
    if (voice.default) score += 1;
    if (score > preferredScore) {
      preferred = voice;
      preferredScore = score;
    }
  }
  return preferredScore >= 4 ? preferred : null;
}

function configureSpeechAudio(audio: HTMLAudioElement, lang: 'zh-CN' | 'en-US') {
  const speechAudio = audio as HTMLAudioElement & { webkitPreservesPitch?: boolean };
  speechAudio.preservesPitch = true;
  speechAudio.webkitPreservesPitch = true;
  speechAudio.playbackRate = 1;
}

function getPoemSpeechText(title: string, content: string) {
  const body = content
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => /[，。！？；]$/.test(line) ? line : `${line}，`)
    .join('');
  return `${title.trim()}。${body}`;
}

function getPoemDisplayLines(content: string) {
  const existing = content.split('\n').map((line) => line.trim()).filter(Boolean);
  if (existing.length === 4) return existing;

  const compact = content.replace(/\s+/g, '');
  const fragments = compact.match(/[^，。！？；]+[，。！？；]?/g)?.map((line) => line.trim()).filter(Boolean) ?? [];
  const isRegularVerse = fragments.every((line) => {
    const length = line.replace(/[^\u3400-\u9fff]/g, '').length;
    return length === 5 || length === 7;
  });
  if (!isRegularVerse) return existing;

  if (fragments.length === 4) return fragments;
  if (fragments.length !== 8) return existing;

  return Array.from({ length: 4 }, (_, index) => fragments.slice(index * 2, index * 2 + 2).join(''));
}

export function LearningView({
  state,
  child,
  type,
  onSelectChild,
  onExit,
  onReview,
  onToast
}: {
  state: AppState;
  child: Child;
  type: KnowledgeType;
  onSelectChild: (childId: string) => void;
  onExit: () => void;
  onReview: (itemId: string, result: ReviewResult) => ReviewEvent | null;
  onToast: (message: string) => void;
}) {
  const queue = useMemo(() => getLearningQueue(state, child.id, type), [state, child.id, type]);
  const current = queue[0];
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<{ result: ReviewResult; stars: number; completed: boolean } | null>(null);
  const [song, setSong] = useState<PoemSong | null>(null);
  const [songState, setSongState] = useState<'idle' | 'loading' | 'ready' | 'empty'>('idle');
  const [songPlaying, setSongPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const songAudioRef = useRef<HTMLAudioElement | null>(null);
  const speechTimerRef = useRef<number | null>(null);

  useEffect(() => () => {
    if (speechTimerRef.current) window.clearTimeout(speechTimerRef.current);
    window.speechSynthesis?.cancel();
    audioRef.current?.pause();
    songAudioRef.current?.pause();
  }, []);

  const intervals = state.settings.intervals[type];
  const planIds = getLearningPlanIds(state, child.id);
  const progress = current ? getChildProgress(state, child.id, current.item.id) : undefined;
  const memory = getMemoryLabel(progress, intervals);
  const nextUpcoming = state.progress
    .filter((entry) => entry.childId === child.id && !entry.masteredAt && planIds.has(entry.itemId))
    .map((entry) => ({ entry, item: state.knowledge.find((item) => item.id === entry.itemId) }))
    .filter((entry) => entry.item?.type === type)
    .sort((left, right) => new Date(left.entry.nextReviewAt).getTime() - new Date(right.entry.nextReviewAt).getTime())[0];

  useEffect(() => {
    songAudioRef.current?.pause();
    setSongPlaying(false);
    setSong(null);

    if (type !== 'poem' || !current || current.item.type !== 'poem') {
      setSongState('idle');
      return;
    }

    const controller = new AbortController();
    setSongState('loading');
    void lookupPoemSong(current.item.title, current.item.author || '', controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        setSong(result);
        setSongState(result ? 'ready' : 'empty');
      })
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        setSongState('empty');
      });

    return () => controller.abort();
  }, [current?.item.id, type]);

  const poemLines = current?.item.type === 'poem' ? getPoemDisplayLines(current.item.content) : [];
  const isQuatrain = poemLines.length === 4;

  const stopSong = () => {
    songAudioRef.current?.pause();
    setSongPlaying(false);
  };

  const toggleSong = () => {
    if (!song) return;
    const audio = songAudioRef.current ?? new Audio();
    songAudioRef.current = audio;
    audio.preload = 'metadata';
    audio.onplay = () => setSongPlaying(true);
    audio.onpause = () => setSongPlaying(false);
    audio.onended = () => setSongPlaying(false);
    audio.onerror = () => {
      setSongPlaying(false);
      setSongState('empty');
      onToast('歌曲暂时无法播放，请稍后再试');
    };

    if (songPlaying) {
      stopSong();
      return;
    }

    if (!audio.src.endsWith(song.playUrl)) audio.src = song.playUrl;
    audioRef.current?.pause();
    window.speechSynthesis?.cancel();
    audio.play().catch(() => {
      setSongPlaying(false);
      onToast('歌曲暂时无法播放，请稍后再试');
    });
  };

  const handleResult = (result: ReviewResult) => {
    if (!current || submitting) return;
    setSubmitting(true);
    const event = onReview(current.item.id, result);
    if (event) {
      setFeedback({ result, stars: event.starsDelta, completed: event.completed });
      onToast(
        event.completed
          ? `完成全部记忆，额外奖励 ${state.settings.rewards[type].mastered} 颗星星`
          : `${result === 'known' ? '认识' : '忘记'}，星星 ${event.starsDelta >= 0 ? '+' : ''}${event.starsDelta}`
      );
    }
    window.setTimeout(() => {
      setFeedback(null);
      setSubmitting(false);
    }, 520);
  };

  const speakText = (text: string, lang: 'en-US' | 'zh-CN') => {
    const playAudio = () => {
      const audio = audioRef.current ?? new Audio();
      audioRef.current = audio;
      audio.src = `/api/tts?lang=${encodeURIComponent(lang)}&text=${encodeURIComponent(text)}&v=child-voice-natural-normal-20260928`;
      configureSpeechAudio(audio, lang);
      audio.play().catch(() => onToast('语音播放失败，请检查手机音量或网络'));
    };

    if (speechTimerRef.current) window.clearTimeout(speechTimerRef.current);
    audioRef.current?.pause();
    const isMobileOrTablet = /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent)
      || (navigator.maxTouchPoints > 1 && window.matchMedia('(pointer: coarse)').matches);
    if (!('speechSynthesis' in window) || !('SpeechSynthesisUtterance' in window)) {
      playAudio();
      return;
    }
    const voices = window.speechSynthesis.getVoices();
    const preferredVoice = getPreferredSpeechVoice(voices, lang);
    if (isMobileOrTablet && !preferredVoice) {
      playAudio();
      return;
    }

    let started = false;
    let fellBack = false;
    const utterance = new SpeechSynthesisUtterance(text);
    if (preferredVoice) utterance.voice = preferredVoice;
    utterance.lang = lang;
    utterance.rate = 1;
    utterance.pitch = lang === 'en-US' ? 1.08 : 1.06;
    utterance.volume = 1;

    const fallback = () => {
      if (fellBack) return;
      fellBack = true;
      if (speechTimerRef.current) window.clearTimeout(speechTimerRef.current);
      playAudio();
    };

    utterance.onstart = () => {
      started = true;
      if (speechTimerRef.current) window.clearTimeout(speechTimerRef.current);
    };
    utterance.onerror = () => {
      if (!started) fallback();
    };

    window.speechSynthesis.cancel();
    window.speechSynthesis.resume();
    window.speechSynthesis.speak(utterance);
    speechTimerRef.current = window.setTimeout(() => {
      if (!started) {
        window.speechSynthesis.cancel();
        fallback();
      }
    }, 1100);
  };

  const speakCurrent = () => {
    if (!current) return;
    stopSong();
    const text = current.item.type === 'poem'
      ? getPoemSpeechText(current.item.title, current.item.content)
      : current.item.title;
    speakText(text, current.item.type === 'word' ? 'en-US' : 'zh-CN');
  };


  if (!current) {
    return (
      <div className="learning-shell learning-complete">
        <header className="learning-topbar">
          <button className="icon-button" type="button" onClick={onExit} aria-label="返回首页">
            <ArrowLeft size={22} />
          </button>
          <div>
            <strong>{typeTitles[type]}</strong>
            <small>{child.name}的学习空间</small>
          </div>
          <span className="learning-topbar__spacer" />
        </header>
        <div className="complete-card">
          <div className="complete-card__art">
            <span>🌱</span>
            <i>✨</i>
          </div>
          <span className="eyebrow">本轮完成</span>
          <h1>今天的任务全部完成啦</h1>
          <p>
            {nextUpcoming?.item
              ? `下一次会复习「${nextUpcoming.item.title}」，让小芽再长高一点。`
              : '新的知识种子也在等着你，去学习计划添加一些吧。'}
          </p>
          <div className="complete-card__stars">
            <Star fill="currentColor" size={22} />
            <strong>{child.stars}</strong>
            <span>颗星星</span>
          </div>
          <button className="primary-button" type="button" onClick={onExit}>
            回到首页
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`learning-shell learning-shell--${type}`}>
      <header className="learning-topbar">
        <button className="icon-button" type="button" onClick={onExit} aria-label="退出学习">
          <ArrowLeft size={22} />
        </button>
        <div>
          <strong>{typeTitles[type]}</strong>
          <small>优先复习到期的知识</small>
        </div>
        {state.children.length > 1 ? (
          <div className="learning-topbar__children">
            {state.children.map((entry) => (
              <button
                key={entry.id}
                type="button"
                className={entry.id === child.id ? 'is-active' : ''}
                onClick={() => onSelectChild(entry.id)}
                aria-label={`切换到${entry.name}`}
              >
                <ChildAvatar child={entry} size="small" active={entry.id === child.id} />
              </button>
            ))}
          </div>
        ) : (
          <span className="learning-topbar__spacer" />
        )}
      </header>

      <main className="learning-stage">
        <section className="memory-status">
          <div className="memory-status__icon"><Brain size={20} /></div>
          <div>
            <span>{memory.eyebrow}</span>
            <strong>{memory.detail}</strong>
          </div>
          <div className="memory-dots" aria-label={`记忆进度 ${memory.step}/${memory.total}`}>
            {Array.from({ length: memory.total }, (_, index) => (
              <i key={index} className={index < memory.step ? 'is-done' : ''} />
            ))}
          </div>
        </section>

        <article className={`memory-card memory-card--${type} ${feedback ? `is-${feedback.result}` : ''}`}>
          {type === 'character' ? (
            <div className="character-content">
              <button className="sound-button sound-button--side" type="button" onClick={speakCurrent} aria-label="播放汉字读音">
                <Volume2 size={27} />
              </button>
              <span className="knowledge-label">认一认这个汉字</span>
              <strong className="character-glyph">{current.item.title}</strong>
              {current.item.pronunciation ? <span className="character-pinyin">{current.item.pronunciation}</span> : null}
              {current.item.meaning ? <p>{current.item.meaning}</p> : null}
            </div>
          ) : null}

          {type === 'word' ? <WordLearningPanel item={current.item} onSpeak={speakText} /> : null}

          {type === 'poem' ? (
            <div className="poem-content">
              <button className="sound-button sound-button--corner" type="button" onClick={speakCurrent} aria-label="朗读古诗">
                <Volume2 size={24} />
              </button>
              <header>
                <span>{current.item.dynasty || ''} · {current.item.author || '佚名'}</span>
                <h1>{current.item.title}</h1>
              </header>
              <div className={`poem-lines ${isQuatrain ? 'poem-lines--quatrain' : ''}`}>
                {poemLines.map((line, index) => (
                  <p key={`${line}-${index}`}>{line}</p>
                ))}
              </div>
              <section className={`poem-song-strip is-${songState}`} aria-live="polite">
                <button
                  className="poem-song-strip__play"
                  type="button"
                  onClick={toggleSong}
                  disabled={songState !== 'ready'}
                  aria-label={songPlaying ? '暂停古诗歌曲' : '播放古诗歌曲'}
                >
                  {songState === 'loading' ? <LoaderCircle className="is-spinning" size={23} /> : null}
                  {songState !== 'loading' && songPlaying ? <Pause fill="currentColor" size={22} /> : null}
                  {songState !== 'loading' && !songPlaying ? <Play fill="currentColor" size={22} /> : null}
                </button>
                <div className="poem-song-strip__copy">
                  <span><Music2 size={14} />古诗唱一唱</span>
                  <strong>
                    {songState === 'loading' ? '正在寻找可播放的歌曲' : null}
                    {songState === 'ready' && song ? song.title : null}
                    {songState === 'empty' ? '暂时没有找到可播放的歌曲' : null}
                  </strong>
                  <small>
                    {songState === 'ready' && song ? `${song.artist}${song.album ? ` · ${song.album}` : ''}` : null}
                    {songState === 'loading' ? '网易云音乐公开试听' : null}
                    {songState === 'empty' ? '仍然可以点击右上角跟读古诗' : null}
                  </small>
                </div>
              </section>
            </div>
          ) : null}

          {feedback ? (
            <div className="review-feedback">
              {feedback.result === 'known' ? <Check size={34} /> : <RotateCcw size={34} />}
              <strong>{feedback.result === 'known' ? '记得很牢' : '再记一次'}</strong>
              <span>
                <Star fill="currentColor" size={17} />
                {feedback.stars >= 0 ? '+' : ''}{feedback.stars}
              </span>
              {feedback.completed ? <small>完成全部记忆</small> : null}
            </div>
          ) : null}
        </article>

        <div className="learning-progress">
          <span>本轮还剩 <strong>{queue.length}</strong> 项</span>
          <div>
            <span style={{ width: `${Math.max(4, 100 - Math.min(queue.length * 7, 96))}%` }} />
          </div>
        </div>

        <section className="review-actions">
          <button
            className="review-button review-button--forgot"
            type="button"
            onClick={() => handleResult('forgot')}
            disabled={submitting}
          >
            <RotateCcw size={25} />
            <span>
              <strong>忘记</strong>
              <small>10 分钟后再记</small>
            </span>
          </button>
          <button
            className="review-button review-button--known"
            type="button"
            onClick={() => handleResult('known')}
            disabled={submitting}
          >
            <Check size={27} />
            <span>
              <strong>认识</strong>
              <small>进入下一次复习</small>
            </span>
          </button>
        </section>
      </main>


      {state.children.length > 1 ? (
        <div className="learning-child-switcher-mobile">
          {state.children.map((entry) => (
            <button
              key={entry.id}
              type="button"
              className={entry.id === child.id ? 'is-active' : ''}
              onClick={() => onSelectChild(entry.id)}
            >
              <ChildAvatar child={entry} size="small" />
              {entry.name}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
